import {
  AlertTriangle,
  Bell,
  BellOff,
  CalendarClock,
  Check,
  CheckCheck,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Inbox,
  LoaderCircle,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Utensils,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import type { AppLanguage } from "../../lib/preferences";
import { providerNotificationCopy as copy } from "./provider-notification-view-copy";
import "./provider-notification-view.css";

const RELATIVE_TIME_TICK_MS = 30_000;

export type ProviderNotificationCategory =
  | "booking"
  | "session"
  | "food_order"
  | "approval"
  | "account";

export type ProviderNotificationCategoryFilter = "all" | ProviderNotificationCategory;

export type ProviderNotificationTargetKind =
  | "booking"
  | "session"
  | "food-order"
  | "venue"
  | "pending-confirmations"
  | "inbox";

export type ProviderNotificationViewTarget =
  | { availability: "available"; kind: ProviderNotificationTargetKind }
  | { availability: "unavailable"; reason: "stale" | "unauthorized" | "invalid" };

/**
 * Localized, presentation-ready notification data. The integration lane owns
 * mapping API template keys/params into title/body and validating the semantic
 * target before it reaches this boundary.
 */
export interface ProviderNotificationViewItem {
  body?: string;
  category: ProviderNotificationCategory;
  createdAtMs: number;
  id: string;
  read: boolean;
  superseded?: boolean;
  target: ProviderNotificationViewTarget;
  title: string;
}

export type ProviderNotificationInboxState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "refreshing" }
  | { status: "error"; message?: string };

export type ProviderNotificationPaginationState =
  | { status: "idle"; nextCursor: string | null }
  | { status: "loading"; nextCursor: string }
  | { status: "error"; nextCursor: string; message?: string };

export type ProviderNotificationPreferenceValues = Record<ProviderNotificationCategory, boolean>;

export type ProviderNotificationPreferencesState =
  | { status: "loading" }
  | { status: "ready"; values: ProviderNotificationPreferenceValues }
  | {
      status: "saving";
      savingCategory: ProviderNotificationCategory;
      values: ProviderNotificationPreferenceValues;
    }
  | {
      status: "error";
      message?: string;
      values?: ProviderNotificationPreferenceValues;
    };

export interface ProviderNotificationPreferencesBoundary {
  onChange: (category: ProviderNotificationCategory, enabled: boolean) => void;
  onRetry: () => void;
  state: ProviderNotificationPreferencesState;
}

export interface ProviderPendingConfirmationBoundary {
  busy?: boolean;
  count: number;
  onMarkRead: () => void;
  onReview: () => void;
  unread: boolean;
}

export interface ProviderNotificationInboxViewProps {
  initialUnreadOnly?: boolean;
  readBusy?: boolean;
  readError?: boolean;
  activeCategory: ProviderNotificationCategoryFilter;
  inboxState: ProviderNotificationInboxState;
  items: readonly ProviderNotificationViewItem[];
  language: AppLanguage;
  nowMs?: number;
  onCategoryChange: (category: ProviderNotificationCategoryFilter) => void;
  onLoadMore: (cursor: string) => void;
  onMarkAllRead: () => void;
  onMarkItemRead?: (notificationId: string) => void;
  onOpenItem: (item: ProviderNotificationViewItem) => void;
  onRefresh?: () => void;
  pagination: ProviderNotificationPaginationState;
  pendingConfirmation?: ProviderPendingConfirmationBoundary;
  preferences?: ProviderNotificationPreferencesBoundary;
  /** Total unread signal shown in the shell, including pendingConfirmation when applicable. */
  unreadCount: number;
}

const categories: ProviderNotificationCategoryFilter[] = [
  "all",
  "booking",
  "session",
  "food_order",
  "approval",
  "account",
];

const preferenceCategories: ProviderNotificationCategory[] = [
  "booking",
  "session",
  "food_order",
  "approval",
  "account",
];

const categoryIcons = {
  account: CircleUserRound,
  approval: ShieldCheck,
  booking: CalendarClock,
  food_order: Utensils,
  session: Clock3,
} as const;

function formatNumber(value: number, language: AppLanguage) {
  return new Intl.NumberFormat(language === "ar" ? "ar-EG" : "en").format(value);
}

function validDateMs(value: number, fallback: number) {
  if (!Number.isFinite(value)) return fallback;
  return Number.isNaN(new Date(value).getTime()) ? fallback : value;
}

function formatRelativeTime(createdAtMs: number, nowMs: number, language: AppLanguage) {
  const safeCreatedAtMs = validDateMs(createdAtMs, nowMs);
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - safeCreatedAtMs) / 1_000));
  if (elapsedSeconds < 45) return copy(language, "justNow");

  const minutes = Math.max(1, Math.floor(elapsedSeconds / 60));
  const locale = language === "ar" ? "ar-EG" : "en";
  if (language === "ar") {
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "short" });
    if (minutes < 60) return relative.format(-minutes, "minute");
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return relative.format(-hours, "hour");
    const days = Math.floor(hours / 24);
    if (days < 7) return relative.format(-days, "day");
  } else {
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(safeCreatedAtMs));
}

function formatExactTime(createdAtMs: number, language: AppLanguage) {
  const safeCreatedAtMs = validDateMs(createdAtMs, 0);
  return new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(safeCreatedAtMs));
}

function targetActionLabel(kind: ProviderNotificationTargetKind, language: AppLanguage) {
  switch (kind) {
    case "booking":
      return copy(language, "openBooking");
    case "session":
      return copy(language, "openSession");
    case "food-order":
      return copy(language, "openOrder");
    case "venue":
      return copy(language, "openVenue");
    case "pending-confirmations":
      return copy(language, "reviewPending");
    case "inbox":
      return copy(language, "openDetails");
  }
}

function notificationTitle(item: ProviderNotificationViewItem, language: AppLanguage) {
  const title = item.title.trim();
  return title && title !== copy(language, "notifications")
    ? title
    : copy(language, `${item.category}Update`);
}

function unavailableTargetCopy(
  reason: Extract<ProviderNotificationViewTarget, { availability: "unavailable" }>["reason"],
  language: AppLanguage,
) {
  switch (reason) {
    case "stale":
      return copy(language, "staleTarget");
    case "unauthorized":
      return copy(language, "unauthorizedTarget");
    case "invalid":
      return copy(language, "invalidTarget");
  }
}

function CategoryLabel({
  category,
  language,
}: {
  category: ProviderNotificationCategory;
  language: AppLanguage;
}) {
  const Icon = categoryIcons[category];
  return (
    <span className="provider-notification-ledger-category">
      <Icon aria-hidden="true" size={12} strokeWidth={2.1} />
      {copy(language, category)}
    </span>
  );
}

function PendingConfirmationCard({
  boundary,
  language,
}: {
  boundary: ProviderPendingConfirmationBoundary;
  language: AppLanguage;
}) {
  const countLabel = copy(language, boundary.count === 1 ? "pendingOne" : "pendingMany", {
    count: formatNumber(boundary.count, language),
  });

  return (
    <article
      className="provider-notification-ledger-pending"
      data-testid="provider-notification-pending"
      data-unread={boundary.unread ? "true" : "false"}
      role="listitem"
    >
      <div className="provider-notification-ledger-pending-copy">
        <h3>{countLabel}</h3>
        <div className="provider-notification-ledger-pending-actions">
          <button
            className="ops-button"
            data-size="sm"
            data-variant="primary"
            disabled={boundary.busy}
            onClick={boundary.onReview}
            type="button"
          >
            {boundary.busy ? <LoaderCircle aria-hidden="true" className="provider-notification-spin" size={12} /> : null}
            {copy(language, "review")}
          </button>
          {boundary.unread ? (
            <button
              aria-label={copy(language, "dismissPending")}
              className="provider-notification-ledger-icon-button"
              disabled={boundary.busy}
              onClick={boundary.onMarkRead}
              title={copy(language, "markRead")}
              type="button"
            >
              <Check aria-hidden="true" size={14} strokeWidth={2.4} />
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function NotificationRow({
  item,
  language,
  nowMs,
  onMarkItemRead,
  onOpenItem,
  readBusy,
}: {
  readBusy?: boolean;
  item: ProviderNotificationViewItem;
  language: AppLanguage;
  nowMs: number;
  onMarkItemRead?: (notificationId: string) => void;
  onOpenItem: (item: ProviderNotificationViewItem) => void;
}) {
  const relativeTime = formatRelativeTime(item.createdAtMs, nowMs, language);
  const exactTime = formatExactTime(item.createdAtMs, language);
  const title = notificationTitle(item, language);
  const availableTarget = item.target.availability === "available" ? item.target : null;
  const unavailableTarget = item.target.availability === "unavailable" ? item.target : null;

  const Icon = categoryIcons[item.category];

  return (
    <article
      className="provider-notification-ledger-row pn-row"
      data-category={item.category}
      data-superseded={item.superseded ? "true" : undefined}
      data-unread={item.read ? "false" : "true"}
      role="listitem"
    >
      <span aria-hidden="true" className="pn-icon" data-category={item.category}>
        <Icon size={16} strokeWidth={2.1} />
      </span>
      {availableTarget ? (
        <button
          aria-label={`${targetActionLabel(availableTarget.kind, language)}: ${title}`}
          className="pn-open"
          data-muted={item.superseded ? "true" : undefined}
          data-testid="provider-notification-item"
          data-unread={item.read ? "false" : "true"}
          onClick={() => onOpenItem(item)}
          type="button"
        >
          <span className="provider-notification-ledger-row-title pn-title">{title}</span>
          {item.body ? <span className="pn-body">{item.body}</span> : null}
          <time
            aria-label={exactTime}
            className="pn-time"
            dateTime={new Date(validDateMs(item.createdAtMs, 0)).toISOString()}
            title={exactTime}
          >
            {relativeTime}
          </time>
        </button>
      ) : (
        <div
          className="pn-open"
          data-muted={item.superseded ? "true" : undefined}
          data-testid="provider-notification-item"
          data-unavailable="true"
          data-unread={item.read ? "false" : "true"}
        >
          <span className="provider-notification-ledger-row-title pn-title">{title}</span>
          {item.body ? <span className="pn-body">{item.body}</span> : null}
          <span className="pn-fallback">
            <AlertTriangle aria-hidden="true" size={12} strokeWidth={2.2} />
            {unavailableTarget ? unavailableTargetCopy(unavailableTarget.reason, language) : null}
          </span>
          <time aria-label={exactTime} className="pn-time" dateTime={new Date(validDateMs(item.createdAtMs, 0)).toISOString()} title={exactTime}>
            {relativeTime}
          </time>
        </div>
      )}
      {!item.read && onMarkItemRead ? (
        <button
          aria-label={copy(language, "markRead")}
          className="pn-mark-read"
          disabled={readBusy}
          onClick={() => onMarkItemRead(item.id)}
          title={copy(language, "markRead")}
          type="button"
        >
          <Check aria-hidden="true" size={14} strokeWidth={2.4} />
        </button>
      ) : null}
    </article>
  );
}

function LoadingLedger({ language }: { language: AppLanguage }) {
  return (
    <div className="provider-notification-ledger-loading" role="status">
      <span className="provider-notification-ledger-visually-hidden">{copy(language, "loading")}</span>
      {[0, 1, 2].map((row) => (
        <span aria-hidden="true" className="provider-notification-ledger-skeleton" key={row}>
          <span />
          <span />
          <span />
        </span>
      ))}
    </div>
  );
}

function InboxError({
  compact,
  language,
  message,
  onRetry,
}: {
  compact: boolean;
  language: AppLanguage;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="provider-notification-ledger-error" data-compact={compact ? "true" : undefined} role="alert">
      <AlertTriangle aria-hidden="true" size={compact ? 14 : 18} strokeWidth={2.1} />
      <div>
        <p>{message ?? copy(language, "errorTitle")}</p>
        {!compact ? <span>{copy(language, "errorBody")}</span> : null}
      </div>
      {onRetry ? (
        <button className="ops-button" data-size="sm" data-variant="secondary" onClick={onRetry} type="button">
          {copy(language, "retry")}
        </button>
      ) : null}
    </div>
  );
}

function PaginationControl({
  language,
  onLoadMore,
  pagination,
}: {
  language: AppLanguage;
  onLoadMore: (cursor: string) => void;
  pagination: ProviderNotificationPaginationState;
}) {
  if (pagination.status === "idle" && pagination.nextCursor === null) return null;

  if (pagination.status === "error") {
    return (
      <div className="provider-notification-ledger-pagination-error" role="alert">
        <span>{pagination.message ?? copy(language, "loadMoreFailed")}</span>
        <button onClick={() => onLoadMore(pagination.nextCursor)} type="button">
          {copy(language, "retry")}
        </button>
      </div>
    );
  }

  const cursor = pagination.nextCursor;
  if (cursor === null) return null;
  const isLoading = pagination.status === "loading";
  return (
    <button
      className="provider-notification-ledger-load-more"
      disabled={isLoading}
      onClick={() => onLoadMore(cursor)}
      type="button"
    >
      {isLoading ? (
        <LoaderCircle aria-hidden="true" className="provider-notification-spin" size={13} />
      ) : (
        <span aria-hidden="true" className="provider-notification-ledger-load-line" />
      )}
      {copy(language, isLoading ? "loadingEarlier" : "loadEarlier")}
    </button>
  );
}

function EmptyLedger({
  activeCategory,
  language,
  onClear,
}: {
  activeCategory: ProviderNotificationCategoryFilter;
  language: AppLanguage;
  onClear: () => void;
}) {
  const isFiltered = activeCategory !== "all";
  return (
    <div className="provider-notification-ledger-empty">
      <span aria-hidden="true" className="provider-notification-ledger-empty-mark">
        <BellOff size={19} strokeWidth={1.8} />
      </span>
      <p>{isFiltered ? copy(language, "emptyFiltered", { category: copy(language, activeCategory) }) : copy(language, "emptyTitle")}</p>
      {!isFiltered ? <span>{copy(language, "emptyBody")}</span> : null}
      {isFiltered ? (
        <button className="ops-button" data-size="sm" data-variant="secondary" onClick={onClear} type="button">
          {copy(language, "clearFilter")}
        </button>
      ) : null}
    </div>
  );
}

function PreferencesPane({
  boundary,
  language,
}: {
  boundary: ProviderNotificationPreferencesBoundary;
  language: AppLanguage;
}) {
  const { state } = boundary;
  const values = state.status === "loading" ? undefined : state.values;
  const hasError = state.status === "error";

  return (
    <div className="provider-notification-ledger-preferences" data-testid="provider-notification-preferences">
      <div className="provider-notification-ledger-preferences-intro">
        <span aria-hidden="true"><Settings2 size={16} strokeWidth={2.1} /></span>
        <div>
          <h2>{copy(language, "pushTitle")}</h2>
          <p>{copy(language, "pushBody")}</p>
        </div>
      </div>

      {state.status === "loading" ? (
        <div className="provider-notification-ledger-preference-loading" role="status">
          <LoaderCircle aria-hidden="true" className="provider-notification-spin" size={16} />
          {copy(language, "pushLoading")}
        </div>
      ) : null}

      {hasError ? (
        <div className="provider-notification-ledger-preference-error" role="alert">
          <span>{state.message ?? (values ? copy(language, "preferenceError") : copy(language, "preferenceLoadError"))}</span>
          <button onClick={boundary.onRetry} type="button">{copy(language, "retry")}</button>
        </div>
      ) : null}

      {values ? (
        <div aria-label={copy(language, "pushTitle")} className="provider-notification-ledger-preference-list" role="group">
          {preferenceCategories.map((category) => {
            const Icon = categoryIcons[category];
            const enabled = values[category];
            const isSaving = state.status === "saving" && state.savingCategory === category;
            return (
              <div className="provider-notification-ledger-preference-row" data-saving={isSaving ? "true" : undefined} key={category}>
                <span aria-hidden="true" className="provider-notification-ledger-preference-icon">
                  <Icon size={14} strokeWidth={2.1} />
                </span>
                <span className="provider-notification-ledger-preference-copy">
                  <strong>{copy(language, category)}</strong>
                  <span>{copy(language, `${category}Description`)}</span>
                </span>
                <button
                  aria-checked={enabled}
                  aria-label={copy(language, enabled ? "turnOff" : "turnOn", { category: copy(language, category) })}
                  className="provider-notification-ledger-switch"
                  disabled={state.status === "saving"}
                  onClick={() => boundary.onChange(category, !enabled)}
                  role="switch"
                  type="button"
                >
                  <span aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      <p className="provider-notification-ledger-inbox-note">
        <Inbox aria-hidden="true" size={13} strokeWidth={2.1} />
        {copy(language, "inboxAlwaysOn")}
      </p>
      {state.status === "saving" ? (
        <p className="provider-notification-ledger-visually-hidden" aria-live="polite" role="status">
          {copy(language, "saving")}
        </p>
      ) : null}
    </div>
  );
}

export function ProviderNotificationInboxView({
  activeCategory,
  initialUnreadOnly = false,
  readBusy = false,
  readError = false,
  inboxState,
  items,
  language,
  nowMs: suppliedNowMs,
  onCategoryChange,
  onLoadMore,
  onMarkAllRead,
  onMarkItemRead,
  onOpenItem,
  onRefresh,
  pagination,
  pendingConfirmation,
  preferences,
  unreadCount,
}: ProviderNotificationInboxViewProps) {
  const [unreadOnly, setUnreadOnly] = useState(initialUnreadOnly);
  const [pane, setPane] = useState<"inbox" | "preferences">("inbox");
  const [clockNowMs, setClockNowMs] = useState(() => suppliedNowMs ?? Date.now());
  const titleId = useId();
  const visiblePane = preferences ? pane : "inbox";
  const nowMs = suppliedNowMs ?? clockNowMs;
  const isRefreshing = inboxState.status === "refreshing";
  const isBusy = inboxState.status === "loading" || isRefreshing || pagination.status === "loading";

  useEffect(() => {
    if (suppliedNowMs !== undefined || items.length === 0) return;
    const timer = window.setInterval(() => setClockNowMs(Date.now()), RELATIVE_TIME_TICK_MS);
    return () => window.clearInterval(timer);
  }, [items.length, suppliedNowMs]);

  const filteredItems = useMemo(
    () => items.filter((item) => (!unreadOnly || !item.read) && (activeCategory === "all" || item.category === activeCategory)),
    [activeCategory, items, unreadOnly],
  );
  const showPending =
    Boolean(pendingConfirmation && pendingConfirmation.count > 0 && (!unreadOnly || pendingConfirmation.unread)) &&
    (activeCategory === "all" || activeCategory === "booking");
  const hasVisibleItems = filteredItems.length > 0 || showPending;
  // Chips only for categories that have something in them (plus the one selected).
  const presentCategories = categories.filter(
    (category) => category !== "all" && (category === activeCategory || items.some((item) => item.category === category) || (category === "booking" && Boolean(pendingConfirmation?.count))),
  ) as ProviderNotificationCategory[];
  const safeUnreadCount = Math.max(0, Math.trunc(unreadCount));
  const countDisplay = safeUnreadCount > 99 ? "99+" : formatNumber(safeUnreadCount, language);

  return (
    <section
      aria-busy={isBusy}
      aria-labelledby={titleId}
      className="provider-notifications provider-notification-ledger"
      data-testid="provider-notifications"
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <header className="provider-notification-ledger-header">
        <div className="provider-notification-ledger-heading">
          <h2 id={titleId}>{copy(language, "notifications")}</h2>
          <p className="provider-notification-ledger-scope">{copy(language, "allVenues")}</p>
        {safeUnreadCount > 0 ? (
          <span
            aria-label={copy(language, "unread", { count: formatNumber(safeUnreadCount, language) })}
            aria-live="polite"
            className="provider-notifications-bell-badge provider-notification-ledger-count"
          >
            {countDisplay}
          </span>
        ) : null}
        </div>
        <div className="provider-notification-ledger-header-actions">
          {preferences ? <button aria-label={copy(language, visiblePane === "inbox" ? "pushAlerts" : "inbox")} aria-pressed={visiblePane === "preferences"} className="provider-notification-ledger-icon-button" onClick={() => setPane(visiblePane === "inbox" ? "preferences" : "inbox")} title={copy(language, visiblePane === "inbox" ? "pushAlerts" : "inbox")} type="button"><Settings2 aria-hidden="true" size={15} /></button> : null}
          {onRefresh ? (
            <button
              aria-label={copy(language, "refresh")}
              className="provider-notification-ledger-icon-button"
              disabled={inboxState.status === "loading" || isRefreshing}
              onClick={onRefresh}
              title={copy(language, "refresh")}
              type="button"
            >
              <RefreshCw aria-hidden="true" className={isRefreshing ? "provider-notification-spin" : undefined} size={13} strokeWidth={2.2} />
            </button>
          ) : null}

        </div>
      </header>

      {visiblePane === "preferences" && preferences ? (
        <PreferencesPane boundary={preferences} language={language} />
      ) : (
        <div className="provider-notification-ledger-inbox">
          <div className="provider-notification-ledger-toolbar pn-toolbar">
            <div aria-label={copy(language, "readViews")} className="pn-segment" role="group">
              <button aria-pressed={unreadOnly} onClick={() => setUnreadOnly(true)} type="button">{copy(language, "unreadView")}</button>
              <button aria-pressed={!unreadOnly} onClick={() => setUnreadOnly(false)} type="button">{copy(language, "allUpdates")}</button>
            </div>
            <button className="pn-mark-all" disabled={readBusy || safeUnreadCount === 0} onClick={onMarkAllRead} type="button">
              <CheckCheck aria-hidden="true" size={14} />
              {copy(language, readBusy ? "clearing" : "markAllRead")}
            </button>
          </div>
          {presentCategories.length > 1 ? (
            <div aria-label={copy(language, "categories")} className="provider-notification-ledger-filters pn-chips" role="group">
              {(["all", ...presentCategories] as ProviderNotificationCategoryFilter[]).map((category) => (
                <button
                  aria-pressed={activeCategory === category}
                  className="pn-chip"
                  key={category}
                  onClick={() => onCategoryChange(category)}
                  type="button"
                >
                  {copy(language, category === "all" ? "allTypes" : category)}
                </button>
              ))}
            </div>
          ) : null}
          {readError ? <p className="provider-notification-ledger-error" role="alert">{copy(language, "readError")}</p> : null}

          {isRefreshing ? (
            <p className="provider-notification-ledger-refreshing" aria-live="polite" role="status">
              <LoaderCircle aria-hidden="true" className="provider-notification-spin" size={12} />
              {copy(language, "refreshing")}
            </p>
          ) : null}

          {inboxState.status === "error" && hasVisibleItems ? (
            <InboxError compact language={language} message={inboxState.message} onRetry={onRefresh} />
          ) : null}

          {inboxState.status === "loading" ? (
            <LoadingLedger language={language} />
          ) : inboxState.status === "error" && !hasVisibleItems ? (
            <InboxError compact={false} language={language} message={inboxState.message} onRetry={onRefresh} />
          ) : !hasVisibleItems ? (
            unreadOnly ? (
              <div className="provider-notification-ledger-empty" role="status">
                <CheckCheck aria-hidden="true" size={22} />
                <h3>{copy(language, safeUnreadCount === 0 ? "caughtUp" : "noUnreadInView")}</h3>
                <p>{copy(language, pagination.nextCursor ? "checkEarlier" : "readHistory")}</p>
                {activeCategory !== "all" ? <button onClick={() => onCategoryChange("all")} type="button">{copy(language, "clearFilter")}</button> : null}
              </div>
            ) : <EmptyLedger activeCategory={activeCategory} language={language} onClear={() => onCategoryChange("all")} />
          ) : (
            <div
              aria-label={copy(language, "notifications")}
              className="provider-notification-ledger-list"
              role="list"
            >
              {showPending && pendingConfirmation ? (
                <PendingConfirmationCard boundary={pendingConfirmation} language={language} />
              ) : null}
              {filteredItems.map((item) => (
                <NotificationRow
                  item={item}
                  key={item.id}
                  language={language}
                  nowMs={nowMs}
                  readBusy={readBusy}
                  onMarkItemRead={onMarkItemRead}
                  onOpenItem={onOpenItem}
                />
              ))}
            </div>
          )}

          {inboxState.status !== "loading" ? (
            <PaginationControl language={language} onLoadMore={onLoadMore} pagination={pagination} />
          ) : null}
        </div>
      )}
    </section>
  );
}
